import {
  Component,
  OnInit,
  signal,
  inject,
  computed,
  ChangeDetectorRef,
  input,
  output,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormControl
} from '@angular/forms';
import { ManualEntryPayload } from '@model/patient/sample-entry.model';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { AddSamplePresenter } from '../../../presenters/add-sample.presenter';
import { DropdownItem } from '@view/shared/molecules/dropdown/dropdown.component';
import { FormDropdownComponent } from '@view/shared/molecules/form-dropdown/form-dropdown.component';
import { InputTextComponent } from '@view/shared/molecules/input-text/input-text.component';
import { FormDateComponent } from '@view/shared/molecules/form-date/form-date.component';
import {
  formatBirthDate,
  mapToDropdownItems,
  IDENTIFIER_TYPE_OPTIONS,
  ADMINISTRATIVE_SEX_OPTIONS,
  toStringOrNull
} from '../shared/utils/sample-form.utils';
import {
  colombianPhoneValidator,
  emailFormatValidator
} from '@view/shared/validators/form-validators';

@Component({
  selector: 'app-edit-sample',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormDropdownComponent,
    InputTextComponent,
    FormDateComponent
  ],
  templateUrl: './edit-sample.component.html',
  styleUrls: [
    '../shared/styles/add-sample.component.scss',
    './edit-sample.component.scss'
  ]
})
export class EditSampleComponent implements OnInit {
  readonly editData = input<ManualEntryPayload | null>(null);
  readonly caseNumber = input<string>('');
  readonly saveSuccess = output();
  readonly cancelEdit = output();

  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly presenter = inject(AddSamplePresenter);
  private readonly cdr = inject(ChangeDetectorRef);

  isLoading = signal(true);
  isSaving = signal(false);
  sampleForm!: FormGroup;
  private masterDataLoaded = false;
  private initialFormValues: Record<string, any> = {};

  constructor() {
    effect(() => {
      const data = this.editData();
      if (data && this.masterDataLoaded) {
        this.populateForm(data);
        this.isLoading.set(false);
      }
    });
  }

  get identifierTypeControl() {
    return this.sampleForm.get('identifierType') as FormControl;
  }
  get idNumberControl() {
    return this.sampleForm.get('idNumber') as FormControl;
  }
  get givenNameControl() {
    return this.sampleForm.get('givenName') as FormControl;
  }
  get middleNameControl() {
    return this.sampleForm.get('middleName') as FormControl;
  }
  get familyNameControl() {
    return this.sampleForm.get('familyName') as FormControl;
  }
  get maternalFamilyNameControl() {
    return this.sampleForm.get('maternalFamilyName') as FormControl;
  }
  get administrativeSexControl() {
    return this.sampleForm.get('administrativeSex') as FormControl;
  }
  get birthDateControl() {
    return this.sampleForm.get('birthDate') as FormControl;
  }
  get emailAddressControl() {
    return this.sampleForm.get('emailAddress') as FormControl;
  }
  get cityControl() {
    return this.sampleForm.get('city') as FormControl;
  }
  get stateControl() {
    return this.sampleForm.get('state') as FormControl;
  }
  get birthCountryControl() {
    return this.sampleForm.get('birthCountry') as FormControl;
  }
  get specimenTypeControl() {
    return this.sampleForm.get('specimenType') as FormControl;
  }
  get specimenIdControl() {
    return this.sampleForm.get('specimenId') as FormControl;
  }
  get specimenContainerIdControl() {
    return this.sampleForm.get('specimenContainerId') as FormControl;
  }
  get examTypeControl() {
    return this.sampleForm.get('examType') as FormControl;
  }
  get priorityIdControl() {
    return this.sampleForm.get('priorityId') as FormControl;
  }
  get regionalControl() {
    return this.sampleForm.get('regional') as FormControl;
  }
  get siteControl() {
    return this.sampleForm.get('site') as FormControl;
  }
  get placerOrderNumberControl() {
    return this.sampleForm.get('placerOrderNumber') as FormControl;
  }
  get laboratoriesIdControl() {
    return this.sampleForm.get('laboratoriesId') as FormControl;
  }
  get diagnosesIdControl() {
    return this.sampleForm.get('diagnosesId') as FormControl;
  }
  get specialistsIdControl() {
    return this.sampleForm.get('specialistsId') as FormControl;
  }
  get companyIdControl() {
    return this.sampleForm.get('companyId') as FormControl;
  }
  get providerIdControl() {
    return this.sampleForm.get('providerId') as FormControl;
  }
  get userDataDescriptionControl() {
    return this.sampleForm.get('userDataDescription') as FormControl;
  }
  get descriptionDataSampleControl() {
    return this.sampleForm.get('descriptionDataSample') as FormControl;
  }
  get descriptionDataExamControl() {
    return this.sampleForm.get('descriptionDataExam') as FormControl;
  }
  get clinicalHistoryControl() {
    return this.sampleForm.get('clinicalHistory') as FormControl;
  }

  specimenTypeDropdownOptions = signal<DropdownItem[]>([]);
  specimenContainerDropdownOptions = signal<DropdownItem[]>([]);
  siteDropdownOptions = signal<DropdownItem[]>([]);
  examTypeDropdownOptions = signal<DropdownItem[]>([]);
  regionDropdownOptions = signal<DropdownItem[]>([]);
  laboratoriesDropdownOptions = signal<DropdownItem[]>([]);
  diagnosesDropdownOptions = signal<DropdownItem[]>([]);
  specialistsDropdownOptions = signal<DropdownItem[]>([]);
  companyDropdownOptions = signal<DropdownItem[]>([]);
  providerDropdownOptions = signal<DropdownItem[]>([]);
  priorityDropdownOptions = signal<DropdownItem[]>([]);

  identifierTypeDropdownOptions = computed(() => IDENTIFIER_TYPE_OPTIONS);

  administrativeSexDropdownOptions = computed(() => ADMINISTRATIVE_SEX_OPTIONS);

  ngOnInit(): void {
    this.initializeForm();
    this.loadMasterDataIfNeeded();
  }

  private loadMasterDataIfNeeded(): void {
    const editDataValue = this.editData();
    if (this.masterDataLoaded) {
      if (editDataValue) {
        this.populateForm(editDataValue);
        this.isLoading.set(false);
      }
      return;
    }

    this.isLoading.set(true);
    this.presenter.loadAllMasterData().subscribe({
      next: (data) => {
        this.populateDropdownOptions(data);
        this.loadAdditionalMasterData();
        this.masterDataLoaded = true;
        const currentEditData = this.editData();
        if (currentEditData) {
          this.populateForm(currentEditData);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toastService.setToast({
          title: 'Error al cargar datos',
          message: 'No se pudieron cargar los catálogos necesarios',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private populateDropdownOptions(data: any): void {
    this.specimenTypeDropdownOptions.set(
      data.specimenTypes.map((item: any) => ({
        label: item.name,
        value: item.id
      }))
    );
    this.siteDropdownOptions.set(
      data.sites.map((item: any) => ({ label: item.name, value: item.id }))
    );
    this.examTypeDropdownOptions.set(
      data.examTypes.map((item: any) => ({
        label: item.name,
        value: item.code
      }))
    );
    this.regionDropdownOptions.set(
      data.regions.map((item: any) => ({ label: item.name, value: item.id }))
    );
    this.diagnosesDropdownOptions.set(
      data.diagnoses.map((item: any) => ({ label: item.name, value: item.id }))
    );
    this.specialistsDropdownOptions.set(
      data.specialists.map((item: any) => ({
        label: item.name,
        value: item.id
      }))
    );
    this.priorityDropdownOptions.set(
      data.priorities.map((item: any) => ({
        label: item.name,
        value: String(item.id)
      }))
    );
    this.laboratoriesDropdownOptions.set(
      data.laboratories.map((item: any) => ({
        label: item.name,
        value: String(item.id)
      }))
    );
    this.cdr.detectChanges();
  }

  private loadAdditionalMasterData(): void {
    this.presenter.loadSpecimenContainers().subscribe({
      next: (containers) =>
        this.specimenContainerDropdownOptions.set(
          mapToDropdownItems(containers)
        )
    });
    this.presenter.loadInsuranceCompanies().subscribe({
      next: (companies) =>
        this.companyDropdownOptions.set(mapToDropdownItems(companies))
    });
    this.presenter.loadProviders().subscribe({
      next: (providers) =>
        this.providerDropdownOptions.set(mapToDropdownItems(providers))
    });
  }

  private initializeForm(): void {
    this.sampleForm = this.fb.group({
      idNumber: [{ value: '', disabled: true }, Validators.required],
      identifierType: [{ value: '', disabled: true }, Validators.required],
      givenName: ['', Validators.required],
      middleName: [''],
      familyName: ['', Validators.required],
      maternalFamilyName: [''],
      emailAddress: ['', [emailFormatValidator()]],
      birthDate: ['', Validators.required],
      administrativeSex: ['', Validators.required],
      maritalStatus: [''],
      race: [''],
      telephoneNumber: ['', [colombianPhoneValidator()]],
      streetAddress: [''],
      city: [''],
      state: [''],
      birthCountry: [''],

      specimenId: [''],
      specimenType: ['', Validators.required],
      specimenContainerId: [''],
      priorityId: ['', Validators.required],

      examType: ['', Validators.required],

      companyId: [''],
      providerId: [''],
      regional: ['', Validators.required],
      site: ['', Validators.required],
      placerOrderNumber: [''],
      consentForUseOfSamples: [false],
      userDataDescription: [''],
      descriptionDataSample: [''],
      descriptionDataExam: [''],
      clinicalHistory: [''],
      prioritization: [false],
      acmg: [false],
      mlpa: [false],
      laboratoriesId: ['', Validators.required],
      diagnosesId: ['', Validators.required],
      specialistsId: ['']
    });

    this.setupUppercaseTransformation();
  }

  private setupUppercaseTransformation(): void {
    const fieldsToUppercase = [
      'givenName',
      'middleName',
      'familyName',
      'maternalFamilyName',
      'userDataDescription',
      'descriptionDataSample',
      'descriptionDataExam',
      'clinicalHistory'
    ];

    fieldsToUppercase.forEach((fieldName) => {
      this.sampleForm
        .get(fieldName)
        ?.valueChanges.subscribe((value: string | null) => {
          if (
            value &&
            typeof value === 'string' &&
            value !== value.toUpperCase()
          ) {
            this.sampleForm.get(fieldName)?.setValue(value.toUpperCase(), {
              emitEvent: false
            });
          }
        });
    });
  }

  private populateForm(data: ManualEntryPayload): void {
    const formValues = {
      ...this.mapPatientData(data.datosPaciente),
      ...this.mapSampleData(data.datosMuestra),
      ...this.mapExamData(data.datosExamen),
      ...this.mapOrderData(data.datosOrden)
    };
    this.sampleForm.patchValue(formValues);

    this.initialFormValues = structuredClone(formValues);
  }

  private mapPatientData(patient: ManualEntryPayload['datosPaciente']) {
    return {
      idNumber: patient.idNumber,
      identifierType: patient.identifierTypeCode,
      givenName: patient.givenName,
      middleName: patient.middleName ?? '',
      familyName: patient.familyName,
      maternalFamilyName: patient.maternalFamilyName ?? '',
      emailAddress: patient.emailAddress ?? '',
      birthDate: patient.birthDate,
      administrativeSex: patient.administrativeSex,
      maritalStatus: patient.maritalStatus ?? '',
      race: patient.race ?? '',
      telephoneNumber: patient.telephoneNumber ?? '',
      streetAddress: patient.streetAddress ?? '',
      city: patient.city,
      state: patient.state,
      birthCountry: patient.country
    };
  }

  private mapSampleData(sample: ManualEntryPayload['datosMuestra']) {
    return {
      specimenId: sample.specimenId ?? '',
      specimenType: sample.specimenTypeId,
      specimenContainerId: sample.specimenContainerId ?? '',
      priorityId: sample.priorityId
    };
  }

  private mapExamData(exam: ManualEntryPayload['datosExamen']) {
    return {
      examType: exam.examId
    };
  }

  private mapOrderData(order: ManualEntryPayload['datosOrden']) {
    return {
      companyId: order.companyId ?? '',
      providerId: order.providerId ?? '',
      regional: order.regionalId,
      site: order.siteId,
      placerOrderNumber: order.placerOrderNumber ?? '',
      consentForUseOfSamples: order.consentForUseOfSamples,
      userDataDescription: order.userDataDescription ?? '',
      descriptionDataSample: order.descriptionDataSample ?? '',
      descriptionDataExam: order.descriptionDataExam ?? '',
      clinicalHistory: order.clinicalHistory ?? '',
      prioritization: order.prioritization,
      acmg: order.acmg,
      mlpa: order.mlpa,
      laboratoriesId: order.laboratoryId,
      diagnosesId: order.diagnosisId,
      specialistsId: order.specialistId ?? ''
    };
  }

  onSave(): void {
    if (this.sampleForm.invalid) {
      this.sampleForm.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();

    this.presenter.updateSample(this.caseNumber(), payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.toastService.setToast({
          title: 'Cambios guardados',
          message: 'La muestra se ha actualizado correctamente',
          type: ToastTypeEnum.SUCCESS
        });
        this.saveSuccess.emit();
      },
      error: (error: any) => {
        this.isSaving.set(false);
        this.toastService.setToast({
          title: 'Error al guardar',
          message:
            error?.error?.message ||
            'No se pudo actualizar la muestra. Intente nuevamente.',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  /**
   * Construye el payload solo con los campos que fueron modificados
   */
  private buildPayload(): any {
    const formValue = this.sampleForm.getRawValue();
    const payload: any = {};

    // Mapeo de campos del formulario a la estructura del backend
    const fieldMappings = {
      datosPaciente: {
        idNumber: 'idNumber',
        identifierType: 'identifierTypeCode',
        givenName: 'givenName',
        middleName: 'middleName',
        familyName: 'familyName',
        maternalFamilyName: 'maternalFamilyName',
        emailAddress: 'emailAddress',
        birthDate: 'birthDate',
        administrativeSex: 'administrativeSex',
        city: 'city',
        state: 'state',
        birthCountry: 'country'
      },
      datosMuestra: {
        specimenType: 'specimenTypeId',
        specimenContainerId: 'specimenContainerId',
        priorityId: 'priorityId'
      },
      datosExamen: {
        examType: 'examId'
      },
      datosOrden: {
        companyId: 'companyId',
        providerId: 'providerId',
        regional: 'regionalId',
        site: 'siteId',
        placerOrderNumber: 'placerOrderNumber',
        consentForUseOfSamples: 'consentForUseOfSamples',
        userDataDescription: 'userDataDescription',
        descriptionDataSample: 'descriptionDataSample',
        descriptionDataExam: 'descriptionDataExam',
        clinicalHistory: 'clinicalHistory',
        prioritization: 'prioritization',
        acmg: 'acmg',
        mlpa: 'mlpa',
        laboratoriesId: 'laboratoryId',
        diagnosesId: 'diagnosisId',
        specialistsId: 'specialistId'
      }
    };

    // Procesar cada sección y agregar solo campos modificados
    for (const [section, fields] of Object.entries(fieldMappings)) {
      const sectionPayload = this.buildSectionPayload(fields, formValue);
      if (Object.keys(sectionPayload).length > 0) {
        payload[section] = sectionPayload;
      }
    }

    return payload;
  }

  private buildSectionPayload(
    fields: Record<string, string>,
    formValue: Record<string, any>
  ): Record<string, any> {
    const sectionPayload: Record<string, any> = {};

    for (const [formField, backendField] of Object.entries(fields)) {
      if (this.hasFieldChanged(formField, formValue[formField])) {
        sectionPayload[backendField] = this.transformFieldValue(
          formField,
          formValue[formField]
        );
      }
    }

    return sectionPayload;
  }

  private transformFieldValue(formField: string, value: any): any {
    if (formField === 'birthDate') {
      return formatBirthDate(value);
    }

    const booleanFields = [
      'consentForUseOfSamples',
      'prioritization',
      'acmg',
      'mlpa'
    ];
    if (booleanFields.includes(formField)) {
      return Boolean(value);
    }

    const nullableStringFields = [
      'specimenContainerId',
      'companyId',
      'providerId',
      'specialistsId'
    ];
    if (nullableStringFields.includes(formField)) {
      return toStringOrNull(value);
    }

    if (typeof value === 'string') {
      return value || '';
    }

    return value;
  }

  /**
   * Compara si un campo ha cambiado respecto a su valor inicial
   */
  private hasFieldChanged(fieldName: string, currentValue: any): boolean {
    const initialValue = this.initialFormValues[fieldName];

    // Normalizar valores para comparación
    const normalizedInitial = this.normalizeValue(initialValue);
    const normalizedCurrent = this.normalizeValue(currentValue);

    return normalizedInitial !== normalizedCurrent;
  }

  /**
   * Normaliza valores para comparación consistente
   */
  private normalizeValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'boolean') {
      return value.toString();
    }
    return String(value).trim();
  }

  onCancel(): void {
    this.cancelEdit.emit();
  }
}
